import * as React from 'react'
import { cn } from '../../lib/cn'

/**
 * MethodChip — read-only pill showing whether an auth method is enabled or
 * disabled.
 *
 * Visual language from the Phase 2 mockup `.method-chip`:
 *   - Pill shape, leading dot indicator
 *   - Enabled: green glow dot + green-tinted border, full opacity
 *   - Disabled: muted dot, reduced opacity + line-through text
 *
 * No raw colours — all values come from ia-* design tokens.
 */
export interface MethodChipProps extends React.HTMLAttributes<HTMLSpanElement> {
  /** Method label to display (e.g. "Password", "Email OTP"). */
  label: string
  /** When true the chip shows the enabled (green) state. */
  enabled: boolean
}

export const MethodChip = React.forwardRef<HTMLSpanElement, MethodChipProps>(
  ({ label, enabled, className, ...props }, ref) => (
    <span
      ref={ref}
      role="img"
      aria-label={`${label}: ${enabled ? 'enabled' : 'disabled'}`}
      className={cn(
        'inline-flex items-center gap-1.5',
        'px-2.5 py-[5px] rounded-pill',
        'font-mono text-[11px]',
        'border transition-colors duration-150',
        enabled
          ? 'bg-ia-success-bg/30 border-ia-success/25 text-ia-text'
          : 'bg-ia-elevated border-ia-border text-ia-text-muted opacity-55',
        className,
      )}
      {...props}
    >
      {/* Leading dot — matches mockup ::before element */}
      <span
        aria-hidden="true"
        className={cn(
          'inline-block h-[6px] w-[6px] rounded-pill shrink-0',
          enabled ? 'bg-ia-success shadow-[0_0_6px_rgba(52,211,153,0.5)]' : 'bg-ia-text-muted',
        )}
      />
      <span className={cn(!enabled && 'line-through decoration-ia-text-muted')}>{label}</span>
    </span>
  ),
)
MethodChip.displayName = 'MethodChip'
